import { Question } from '../types';

export const calculateScore = (
  correctCount: number,
  totalQuestions: number,
  timeSpent: number,
  difficulty: number
): number => {
  const baseScore = correctCount * 10;
  const difficultyBonus = correctCount * difficulty * 3;
  const timeBonus = Math.max(0, Math.floor((totalQuestions * 90 - timeSpent) / 10));
  
  return baseScore + difficultyBonus + timeBonus;
};

export const calculateStars = (accuracy: number): 0 | 1 | 2 | 3 => {
  if (accuracy >= 90) return 3;
  if (accuracy >= 75) return 2;
  if (accuracy >= 60) return 1;
  return 0;
};

export const calculateAccuracy = (
  correctCount: number,
  totalQuestions: number
): number => {
  return Math.round((correctCount / totalQuestions) * 100);
};

export const isChallengePassed = (
  correctCount: number,
  totalQuestions: number
): boolean => {
  const accuracy = calculateAccuracy(correctCount, totalQuestions);
  return accuracy >= 75;
};

export const getRandomQuestions = (
  questions: Question[],
  count: number,
  difficulty?: number
): Question[] => {
  const filtered = difficulty
    ? questions.filter(q => q.difficulty === difficulty)
    : questions;
  
  const shuffled = [...filtered].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

export const getQuestionById = (
  questions: Question[],
  id: string
): Question | undefined => {
  return questions.find(q => q.id === id);
};

export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const getDifficultyLabel = (difficulty: number): string => {
  const labels: { [key: number]: string } = {
    1: '简单',
    2: '较易',
    3: '中等',
    4: '较难',
    5: '困难'
  };
  return labels[difficulty] || '未知';
};

export const getDifficultyColor = (difficulty: number): string => {
  const colors: { [key: number]: string } = {
    1: '#52c41a',
    2: '#73d13d',
    3: '#faad14',
    4: '#fa8c16',
    5: '#f5222d'
  };
  return colors[difficulty] || '#d9d9d9';
};

export const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const saveToLocalStorage = (key: string, data: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

export const loadFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    return defaultValue;
  }
};

export const generateUserId = (): string => {
  return 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

export const isToday = (dateString: string): boolean => {
  const today = new Date();
  const date = new Date(dateString);
  return (
    today.getFullYear() === date.getFullYear() &&
    today.getMonth() === date.getMonth() &&
    today.getDate() === date.getDate()
  );
};

export const getToday = (): string => {
  return new Date().toISOString().split('T')[0];
};
